import { Side } from '@/compiled_proto/com/celertech/positionmanager/api/enums/SideProto';
import { formatOrderMessage } from '@/helpers/orderHelper';
import { useAppSelector } from '@/state/hooks';
import { PairMap } from '@/state/reducers/marketPairSlice';
import { selectActiveTicker } from '@/state/reducers/tickerSlice';
import { useEffect, useMemo, useState } from 'react';
import { UseCurrencyReturn, useCurrency } from './useCurrency';
import { UseInstrumentReturn, useInstrument } from './useInstrument';
import useOrderBook, { UseOrderBookReturn } from './useOrderBook';

export interface UseOrderExecutionTraderReturn {
    ccy1: string;
    ccy2: string;
    sideState: [Side, React.Dispatch<React.SetStateAction<Side>>];
    currencyState: [string, React.Dispatch<React.SetStateAction<string>>];
    activeTicker: PairMap;
    orderBook: UseOrderBookReturn;
    orderMessage: string;
    instrument: UseInstrumentReturn;
    isCcy2Order: boolean;
    orderDecimals: number;
    minOrderSize: number;
    ccy1Config: UseCurrencyReturn;
    ccy2Config: UseCurrencyReturn;
    activeCurrencyConfig: UseCurrencyReturn;
}

const useOrderExecutionTrader = (): UseOrderExecutionTraderReturn => {
    const activeTicker = useAppSelector(selectActiveTicker);
    const [ccy1, ccy2] = useMemo(() => activeTicker.celer.split('/'), [activeTicker.celer]);

    const sideState = useState<Side>(Side.BUY);
    const currencyState = useState<string>(ccy1);

    const ccy1Config = useCurrency(ccy1);
    const ccy2Config = useCurrency(ccy2);

    const isCcy2Order = useMemo(() => currencyState[0] === ccy2, [currencyState[0], ccy2]);

    const instrument = useInstrument(activeTicker.celer);
    const orderBook = useOrderBook(activeTicker.celer, sideState[0], isCcy2Order);

    const activeCurrencyConfig = useMemo(
        () => (currencyState[0] === ccy1 ? ccy1Config : ccy2Config),
        [currencyState[0]]
    );

    const orderMessage = useMemo(
        () => formatOrderMessage({ side: sideState[0], isCcy2Order, ccy1Config, ccy2Config }),
        [sideState[0], isCcy2Order, ccy1Config, ccy2Config]
    );

    const { orderDecimals, minOrderSize } = useMemo(() => {
        const isCcyConfig = ['FX', 'Crypto'].includes(instrument?.type || '');

        return {
            orderDecimals: isCcyConfig ? activeCurrencyConfig.order_decimals : instrument.quantityDecimals ?? 0,
            minOrderSize: isCcyConfig ? activeCurrencyConfig.min_order_size : instrument.min_order_size ?? 0
        };
    }, [instrument, activeCurrencyConfig]);

    useEffect(() => {
        currencyState[1](ccy1);
    }, [activeTicker.celer]);

    return {
        ccy1,
        ccy2,
        sideState,
        currencyState,
        orderBook,
        orderMessage,
        instrument,
        activeCurrencyConfig,
        activeTicker,
        orderDecimals,
        minOrderSize,
        ccy1Config,
        ccy2Config,
        isCcy2Order
    };
};

export default useOrderExecutionTrader;
